import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import ToggleSwitch from 'components/ToggleSwitch';
import {
  useGetPrivacyPreferencesQuery,
  useUpdatePrivacyPreferencesMutation,
  useGetFeedPreferencesQuery,
  useUpdateFeedPreferencesMutation,
  PrivacyPreferences,
} from 'store/services/settings/settingsApi';
import { useGetProfileSettingsQuery, useUpdateProfileSettingsMutation } from 'store/services/profile/profileApi';
import { SettingsCard, SettingsRow, SettingsSection, VacationChip } from '../SettingsRow/SettingsRow';
import { useVacationMode } from 'hooks/useVacationMode';
import { formatVacationDate } from 'utils/vacation';
import './PrivacyTab.css';

type PrivacyKey =
  | 'allowFollowers'
  | 'showWantActivityToFollowers'
  | 'showTradeActivityToFollowers'
  | 'allowIncomingTradeOffers';

const SAVE_FAILED = 'Failed to update privacy setting. Please try again.';

/**
 * Settings > Privacy: every visibility control in one place. The profile
 * visibility toggles (Have List, Contact button) and the community feed toggle
 * moved here from Public Profile and Preferences; they still write to their own
 * endpoints. Toggles save on change; a failed save says so and the switch,
 * which renders from server state, falls back on its own.
 */
const PrivacyTab: React.FC = () => {
  const { data, isLoading } = useGetPrivacyPreferencesQuery();
  const [updatePreferences, { isLoading: isUpdating }] = useUpdatePrivacyPreferencesMutation();
  const { data: profileSettings, isLoading: isLoadingProfileSettings } = useGetProfileSettingsQuery();
  const [updateProfileSettings, { isLoading: isUpdatingProfileSettings }] = useUpdateProfileSettingsMutation();
  const { data: feedPreferences, isLoading: isLoadingFeed } = useGetFeedPreferencesQuery();
  const [updateFeedPreferences, { isLoading: isUpdatingFeed }] = useUpdateFeedPreferencesMutation();

  const handleToggle = useCallback(
    async (key: PrivacyKey, value: boolean) => {
      try {
        await updatePreferences({ [key]: value } as Partial<PrivacyPreferences>).unwrap();
      } catch {
        toast.error(SAVE_FAILED);
      }
    },
    [updatePreferences],
  );

  const handleProfileToggle = async (key: 'showHaveListOnProfile' | 'showContactButtonOnProfile', value: boolean) => {
    try {
      await updateProfileSettings({ [key]: value }).unwrap();
    } catch {
      toast.error(SAVE_FAILED);
    }
  };

  const handleShowInFeedToggle = async (value: boolean) => {
    try {
      await updateFeedPreferences({ showInFeed: value }).unwrap();
    } catch {
      toast.error(SAVE_FAILED);
    }
  };

  const vacation = useVacationMode();
  const disabled = isLoading || isUpdating;
  const profileDisabled = isLoadingProfileSettings || isUpdatingProfileSettings;

  return (
    <div className="privacy-tab">
      <SettingsSection label="Your profile">
        <SettingsCard>
          <SettingsRow
            title="Show my Have List on my profile"
            description="Other traders can browse the cards you've marked for trade. Cards marked for trade can still appear in trade suggestions and offers either way."
            control={
              <ToggleSwitch
                checked={profileSettings?.showHaveListOnProfile ?? false}
                onChange={(v) => handleProfileToggle('showHaveListOnProfile', v)}
                disabled={profileDisabled}
                ariaLabel="Show my Have List on my profile"
              />
            }
          />
          <SettingsRow
            title="Show a Contact button"
            description="Others can contact you from your public profile."
            control={
              <ToggleSwitch
                checked={profileSettings?.showContactButtonOnProfile ?? false}
                onChange={(v) => handleProfileToggle('showContactButtonOnProfile', v)}
                disabled={profileDisabled}
                ariaLabel="Show a Contact button"
              />
            }
          />
          <SettingsRow
            title="Show my trades in the community feed"
            description="Your trades appear in the public activity feed visible to all members."
            control={
              <ToggleSwitch
                checked={feedPreferences?.showInFeed ?? true}
                onChange={handleShowInFeedToggle}
                disabled={isLoadingFeed || isUpdatingFeed}
                ariaLabel="Show my trades in the community feed"
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Trade offers">
        <SettingsCard>
          {/*
            Turning this off also stops the user SENDING offers (reciprocity),
            which the description states so the switch is not a surprise.
            `?? true` because the flag is opt-OUT.
          */}
          <SettingsRow
            title={
              <>
                Allow others to send me trade offers
                {/* Vacation overrides this at enforcement time and never writes it, so
                    turning vacation off restores whatever is stored here. */}
                {vacation.active && (
                  <VacationChip>
                    {vacation.until ? `Paused until ${formatVacationDate(vacation.until, { weekday: false })}` : 'Paused'}
                  </VacationChip>
                )}
              </>
            }
            description="When off, existing offers are unaffected but new offers are blocked."
            control={
              <ToggleSwitch
                checked={data?.allowIncomingTradeOffers ?? true}
                onChange={(v) => handleToggle('allowIncomingTradeOffers', v)}
                disabled={disabled}
                ariaLabel="Allow others to send me trade offers"
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Followers">
        <SettingsCard>
          <SettingsRow
            title="Allow others to follow me"
            description="When off, existing followers are unaffected but new follows are blocked."
            control={
              <ToggleSwitch
                checked={data?.allowFollowers ?? true}
                onChange={(v) => handleToggle('allowFollowers', v)}
                disabled={disabled}
                ariaLabel="Allow others to follow me"
              />
            }
          />
          <SettingsRow
            title="Show my want list additions to followers"
            description="Followers can see when you add cards to your want list."
            control={
              <ToggleSwitch
                checked={data?.showWantActivityToFollowers ?? true}
                onChange={(v) => handleToggle('showWantActivityToFollowers', v)}
                disabled={disabled}
                ariaLabel="Show my want list additions to followers"
              />
            }
          />
          <SettingsRow
            title="Show my trade completions to followers"
            description="Followers can see when you complete a trade."
            control={
              <ToggleSwitch
                checked={data?.showTradeActivityToFollowers ?? true}
                onChange={(v) => handleToggle('showTradeActivityToFollowers', v)}
                disabled={disabled}
                ariaLabel="Show my trade completions to followers"
              />
            }
          />
        </SettingsCard>
      </SettingsSection>
    </div>
  );
};

export default PrivacyTab;
